import React, {useContext} from 'react';
import PropTypes from 'prop-types';

import {Dropdown} from '@homeaway/react-dropdown';
import {SVGIcon} from '@homeaway/react-svg';
import {CHEVRON_DOWN__16, PERSON__24} from '@homeaway/svg-defs';
import {TrackingContext} from '@homeaway/react-event-tracking';

import {clickedOnTravelerLogin, clickedOnOwnerLogin} from '../../utils/login.utils';
import {linkLocations, namedEvents} from '../../utils/edap';
import {loginRegex} from '../../utils/regex';

function Login({items, label, travelerLoginClickHandler}) {
    // Add the context to be used for pushing edap events
    const context = useContext(TrackingContext);

    function edapTrigger(item) {
        let actionLocation = linkLocations.HEADER_LINK_LOGIN; // default
        if (clickedOnTravelerLogin(item.cssClass) || clickedOnOwnerLogin(item.cssClass)) {
            actionLocation = clickedOnTravelerLogin(item.cssClass) ? linkLocations.HEADER_LINK_TRAVELER_LOGIN : linkLocations.HEADER_LINK_OWNER_LOGIN;
        }
        const eventField = {
            actionlocation: actionLocation
        };
        context.trigger(namedEvents.HEADER_LINK_CLICK, eventField);
    }

    function createLoginHandler(item) {
        return (event) => {
            if (!item.link) {
                return;
            }
            edapTrigger(item);

            const didClickOnTravelerLogin = clickedOnTravelerLogin(item.cssClass);
            if (travelerLoginClickHandler && didClickOnTravelerLogin) {
                event.stopPropagation();
                event.preventDefault();
                travelerLoginClickHandler();
                return;
            }

            // CAS Login URLs need to be double encoded because of how their params are structured:
            // https://{DOMAIN}/auth/traveler/login?service=https://{DOMAIN}/exp/sso/auth?lt=traveler&context=def&service={DOUBLE_ENCODED_RETURN_URL}&kmsi=true
            let doubleEncodedURI = encodeURIComponent(encodeURIComponent(window.location.pathname + window.location.search));

            let loginRE = loginRegex.LOGIN_RE;
            // if login, perform specific link update
            let newLink = item.link.replace(
                loginRE,
                `$1${doubleEncodedURI}&kmsi=true`
            );

            // if we generate a new link, user has navigated to a sub URL in the same application
            // and we should redirect them there.  Otherwise let default user-agent behavior handle
            // rendered link
            if (newLink !== item.link) {
                event.stopPropagation();
                event.preventDefault();
                window.location.assign(newLink);
            }
        };
    }

    function handleLoginClick() {
        edapTrigger({});
    }

    // if there are no links then render nothing
    if (!items) {
        return null;
    }

    const id = 'site-header__login';

    const toggle = (
        <button
            aria-expanded="false"
            aria-haspopup="true"
            className="site-header-nav__toggle Dropdown__toggle"
            id={id}
        >
            <SVGIcon inlineFlex markup={PERSON__24}/><span className="site-header-nav__icon-label">{label}</span><SVGIcon className="Dropdown__toggle-icon" inlineFlex markup={CHEVRON_DOWN__16}/>
        </button>
    );

    return (
        <Dropdown className="site-header-nav__dropdown" customToggle={toggle} dropdownRight id={id} label={label} onDropdownShow={handleLoginClick}>
            {items.map((item, index) => (
                <li className="site-header-login__item" key={index} role={'presentation'}>
                    <a className={item.cssClass} data-bypass="true" href={item.link} onClick={createLoginHandler(item)} role={'menuitem'} rel="nofollow">{item.text}</a>
                </li>
            ))}
        </Dropdown>
    );
}

Login.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({
        cssClass: PropTypes.string,
        link: PropTypes.string,
        text: PropTypes.string.isRequired
    })),
    label: PropTypes.string.isRequired,
    travelerLoginClickHandler: PropTypes.func
};

export {Login};

